import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Input, Select, notification, DatePicker } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { LocalStorageService } from "../../../../../base/services/local-storage.service";
import { httpService } from "../../../../../base/services/httpService.service";
import { withErrorBoundary } from "../../../../../components/ErrorBoundary/withErrorBoundary";
import { notifyPlanLimitRefusal } from "../../../../../components/Billing/planLimits";
import NewMemberTooltip from "../../../../admin/components/user-management/components/NewMemberTooltip/NewMemberTooltip";

const { TextArea } = Input;

export default function ProjectTypes() {
   const workspcId = LocalStorageService?.getItem("currentWorkspaceId") || "";

   const { id } = useParams();
   const navigate = useNavigate();

   const [load, setLoad] = useState(false);
   const [dataList, setDataList] = useState([]);
   const [countries, setCountries] = useState([]);

   useEffect(() => {
      // Sent with the selected organization's X-OrganizationId, as every
      // request is (base/utils/http.js). Without one the server answers 400,
      // and 404 for a workspace of another organization; either is said
      // rather than leaving the manager list empty. A refusal answered as a
      // 200 is already shown by the interceptor.
      httpService
         .get("/Members/getAllOrgMembersInWorkspace/?WorkSpaceId=" + workspcId)
         .then((res) => {
            setDataList(
               (res.data ?? []).map((member) => ({
                  id: member.id,
                  fullName: member.fullName,
                  role: (member.roles ?? []).map((role) => role.name)
               }))
            );
         })
         .catch((failure) => {
            if (!failure?.response) return;
            const answer = failure.response.data;
            notification.error({
               message: "Oops! A problem has occurred!",
               description:
                  (typeof answer?.message === "string" && answer.message) ||
                  (Array.isArray(answer?.errors) && answer.errors.find(Boolean)) ||
                  "We couldn't load this workspace's members, please try again."
            });
         });
      httpService.get("/Languages/getAllCountries", (res) => {
         setCountries(
            res.data
               .map((country) => ({
                  id: country.id,
                  name: country.name,
                  role: country.code
               }))
               .sort((a, b) => a.name.localeCompare(b.name))
         );
      });
   }, [workspcId]);

   const onFinish = (values) => {
      setLoad(true);
      let formattedStartDate;
      let formattedEndDate;
      if (values.deadline) {
         // Only a deadline (end date) is picked; the project starts now.
         formattedStartDate = dayjs().format();
         formattedEndDate = values.deadline.format();
      }
      const payload = {
         name: values.projectTitle,
         description: values.projectDescription,
         managerId: values.projectManager,
         members: values.dataCollectors,
         workspaceId: LocalStorageService?.getItem("currentWorkspaceId") || "",
         typeId: id,
         countryIds: values.country,
         startDate: formattedStartDate,
         endDate: formattedEndDate
      };
      httpService
         .post(
            "/Projects/add",
            payload,
            (res) => {
               const createdProjectId = res.data;

               LocalStorageService.setItem("currentProjectID", createdProjectId);
               navigate("/dashboard");
            },
            (error) => {
               if (!notifyPlanLimitRefusal(error)) {
                  notification.error({
                     message: "Error",
                     description: "Please try again"
                  });
               }
               setLoad(false);
            }
         )
         .finally(() => {
            setLoad(false);
         });
   };

   const onFinishFailed = (errorInfo) => {
      setLoad(false);
   };

   const textAreaStyle = { resize: "none" };

   return (
      <div className="create-new-project-page flex-column align-items-center ">
         <h2
            className="text-center d-flex align-items-center justify-content-center mt-3"
            style={{ padding: "50px" }}
         >
            New project
         </h2>

         <Form name="basic" onFinish={onFinish} onFinishFailed={onFinishFailed} autoComplete="off">
            <Form.Item
               label="Project Title*"
               name="projectTitle"
               rules={[
                  {
                     required: true,
                     message: "Please specify the official title of the project."
                  }
               ]}
            >
               <Input placeholder="Enter a title" />
            </Form.Item>

            <Form.Item
               label="Project Description"
               name="projectDescription"
               rules={[
                  {
                     required: false,
                     message: "Please furnish a detailed description of the project for clarity."
                  }
               ]}
            >
               <TextArea
                  rows="6"
                  style={textAreaStyle}
                  placeholder="Enter a description"
               ></TextArea>
            </Form.Item>

            <Form.Item
               label={
                  <span>
                     <span>Project Manager* </span>
                     {dataList.filter(
                        (member) => member.role[0] !== "DataCollector" && member.role[0] !== "Guest"
                     ).length < 1 && (
                        <NewMemberTooltip
                           title="No members"
                           icon={
                              <InfoCircleOutlined style={{ color: "#878787", cursor: "pointer" }} />
                           }
                           summary="Add users via Users Management to proceed."
                        />
                     )}
                  </span>
               }
               name="projectManager"
               rules={[
                  {
                     required: true,
                     message: "Specify the name and designation of the assigned project manager."
                  }
               ]}
            >
               <Select
                  placeholder="--Select a manager--"
                  allowClear
                  showSearch
                  optionFilterProp="children"
               >
                  {Array.isArray(dataList) &&
                     dataList
                        .filter(
                           (member) =>
                              member.role[0] !== "DataCollector" && member.role[0] !== "Guest"
                        )
                        .map((data, i) => {
                           return (
                              <Select.Option value={data.id} key={i}>
                                 {data.fullName}
                              </Select.Option>
                           );
                        })}
               </Select>
            </Form.Item>
            <Form.Item
               label="Team Members"
               name="dataCollectors"
               rules={[
                  {
                     required: false,
                     message:
                        "For documentation purposes, provide a roster of project team members."
                  }
               ]}
            >
               <Select
                  placeholder="--Select members-- "
                  mode="multiple"
                  showSearch
                  optionFilterProp="children"
               >
                  {dataList.map((data, i) => (
                     <Select.Option value={data.id} key={i}>
                        {data.fullName}
                     </Select.Option>
                  ))}
               </Select>
            </Form.Item>
            <Form.Item
               label="Country*"
               name="country"
               rules={[
                  {
                     required: true,
                     message: "Specify the country of origin or primary activity for the project."
                  }
               ]}
            >
               <Select
                  placeholder="--Select countries-- "
                  mode="multiple"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                     option.children.toLowerCase().startsWith(input.toLowerCase())
                  }
               >
                  {countries.map((data, i) => {
                     return (
                        <Select.Option value={data.id} key={i}>
                           {data.name}
                        </Select.Option>
                     );
                  })}
               </Select>
            </Form.Item>
            <Form.Item
               label="Deadline*"
               name="deadline"
               rules={[
                  {
                     required: true,
                     message: "Please select a deadline for the project."
                  }
               ]}
            >
               <DatePicker />
            </Form.Item>

            <div className="create-survey-button-group" style={{ marginTop: "30px" }}>
               <Form.Item
                  wrapperCol={{
                     offset: 8,
                     span: 16
                  }}
               >
                  <Button type="primary" onClick={() => navigate(-1)} className="defaultButton">
                     Cancel
                  </Button>
               </Form.Item>

               <Form.Item
                  wrapperCol={{
                     offset: 8,
                     span: 16
                  }}
               >
                  <Button
                     disabled={load}
                     loading={load}
                     htmlType="submit"
                     className="primaryButton"
                  >
                     Create project
                  </Button>
               </Form.Item>
            </div>
         </Form>
      </div>
   );
}

const ProjectTypesWithErrorBoundary = withErrorBoundary(ProjectTypes);
export { ProjectTypesWithErrorBoundary as ProjectTypes };
